import { useMemo } from "react";
import { useServerTable } from "../../../components/table/tableFeatures";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import {
  useGetV4ProductReportByProduct,
  type ProductQueryV4Row,
} from "../../../modules/perf_reports/api/product/v4ProductReportByProduct";
import { useGetV4ProductReportOptions } from "../../../modules/perf_reports/api/product/v4ProductReportOptions";
import { css } from "../../../../styled-system/css";
import { HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { Button } from "../../../components/core/basic/Button";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { idsToVisibilityState, resetTable, selectionStateToSelectedIds } from "../../../components/table/utils";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { RT8FilterWidget } from "../../../modules/merchandising/components/tables/FilterWidget";
import { BulkActionButton, type BulkActionOptions } from "../../../components/table/BulkActionButton";
import { useStandardEntityMetrics } from "../../../modules/merchandising/components/tables/metricUtils";
import { useSimpleCan } from "../../../config/casl/casl";
import { ProductCell } from "../../../modules/merchandising/components/cells/ProductCell";
import { INVENTORY_STATES, SALE_STATES } from "../../../components/core/blocks/AdPlan/constants";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "../../../components/custom/merchandise/constants";
import { toTitleCase } from "../../../utils/formatText";
import { useNavigate } from "@tanstack/react-router";
import { Badge } from "react-bootstrap";
import { useAppSelector } from "../../../redux/hooks";
import api from "../../../utils/api";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useTagFilterOptions } from "../../../modules/tags/api/useTagFilterOptions";
import { OverflowTags } from "../../../components/core/blocks/OverflowTags";

const productColumnHelper = createColumnBuilder<ProductQueryV4Row>();

function normalizeInventoryState(row: ProductQueryV4Row): string {
  const { stock_status, inventory_state } = row;
  if (inventory_state === "OVERSTOCK") return "OVERSTOCK";
  if (stock_status === "IN_STOCK_SCARCE") return "LOW_STOCK";
  return stock_status || inventory_state || "IN_STOCK";
}

const DEFAULT_VISIBLE_COLUMNS = [
  "product_title",
  "sales",
  "units",
  "seo_score",
  "buyability_score",
  "ctr",
  "conversion_rate",
  "ad_spend",
  "ad_sales",
  "acos",
  "clicks",
  "impressions",
  "orders",
  "total_acos",
];

interface ContentProductsTableProps {
  category: string;
}

export const ContentProductsTable = ({ category }: ContentProductsTableProps) => {
  const { t } = useTranslation(["product_quality", "common", "advertising"]);
  const standardMetricIds = useStandardEntityMetrics("content");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const navigate = useNavigate();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const userCanUpdateProducts = useSimpleCan("update", "Product");

  const createTemplate = useMutation({
    mutationFn: async (productIds: number[]) => {
      const ch = channel.currentChannel;
      return await api.post("/merchandise/content_templates/", {
        product_ids: productIds,
        market_place: marketPlace.marketPlace,
        channel: ch === "amazon" ? "AMZ" : ch,
      });
    },
    onSuccess: (res) => {
      if (res.status === 201) {
        const data = res.data as { id: string };
        void navigate({ to: "/user/content/templates/$templateId", params: { templateId: data.id } });
      }
    },
  });

  const bulkActionOptions: BulkActionOptions<string>[] = [
    {
      label: (
        <>
          {t(($) => $.product_quality["Create Template"])} <Badge>{t(($) => $.product_quality["ALPHA"])}</Badge>
        </>
      ),
      action: (selected) => {
        if (selected === "all") return;
        createTemplate.mutate(selected.map(Number));
      },
      disabled: createTemplate.isPending,
    },
  ];

  const columns = useMemo(
    () => [
      productColumnHelper.accessor("product_title", {
        id: "product_title",
        header: t(($) => $.product_quality["Product"]),
        minSize: 250,
        size: 400,
        maxSize: 600,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <ProductCell
              value={r.product_title}
              to={`/user/content/seo?id=${r.product_id}`}
              className={css({ overflowX: "hidden" })}
              original={{
                product_id: r.product_id.toString(),
                asin: r.asin,
                sku: r.sku,
                sales_state: r.sales_state,
                stock_status: r.stock_status,
                inventory_state: r.inventory_state,
                tags: r.tags,
                tags_enhanced: r.tags_enhanced,
                primary_variant: null,
                cellContext: "content",
                pricing_plan__status: null,
              }}
            />
          );
        },
        meta: {
          columnSelectorOptions: {
            label: t(($) => $.product_quality["Product"]),
          },
        },
        enableColumnFilter: false,
      }),
      ...standardMetricIds.map((id) => productColumnHelper.metric(id)),
      productColumnHelper.accessor((row: ProductQueryV4Row) => normalizeInventoryState(row), {
        id: "stock_status",
        header: t(($) => $.product_quality["Inventory State"]),
        cell: ({ getValue }) => {
          const sel = INVENTORY_STATES[getValue()]?.label;
          return sel ? t(sel) : getValue();
        },
        meta: {
          columnFilterOptions: { format: "select", label: t(($) => $.product_quality["Inventory State"]) },
          columnSelectorOptions: { label: t(($) => $.product_quality["Inventory State"]) },
        },
      }),
      productColumnHelper.accessor("sales_state", {
        id: "sales_state",
        header: t(($) => $.product_quality["Sales State"]),
        cell: ({ getValue }) => {
          const sel = SALE_STATES[getValue()]?.label;
          return sel ? t(sel) : getValue();
        },
        meta: {
          columnFilterOptions: { format: "select", label: t(($) => $.product_quality["Sales State"]) },
          columnSelectorOptions: { label: t(($) => $.product_quality["Sales State"]) },
        },
      }),
      tagsEnhancedEnabled ?
        productColumnHelper.accessor((row: ProductQueryV4Row) => row.tags_enhanced, {
          id: "tags_enhanced.id",
          header: t(($) => $.common.tags.Tags),
          cell: ({ row }) => <OverflowTags enhancedTags={row.original.tags_enhanced} id={row.original.product_id} />,
          meta: {
            columnFilterOptions: { format: "multi-select", label: t(($) => $.common.tags.Tags) },
            columnSelectorOptions: { label: t(($) => $.common.tags.Tags) },
          },
        })
      : productColumnHelper.accessor((row: ProductQueryV4Row) => row.tags, {
          id: "tags",
          header: t(($) => $.common.tags.Tags),
          cell: ({ getValue }) => getValue().join(", "),
          meta: {
            columnFilterOptions: { format: "multi-select", label: t(($) => $.common.tags.Tags) },
            columnSelectorOptions: { label: t(($) => $.common.tags.Tags) },
          },
        }),
      productColumnHelper.accessor(
        (row: ProductQueryV4Row & { variations?: Record<string, string> }) => {
          const variations = row.variations ?? {};
          return Object.entries(variations).map(([k, v]) => `${k} - ${v}`);
        },
        {
          id: "variations",
          header: t(($) => $.product_quality["Variants"]),
          cell: ({ getValue }) => getValue().join(", "),
          meta: {
            columnFilterOptions: { format: "multi-select", label: t(($) => $.product_quality["Variants"]) },
            columnSelectorOptions: { label: t(($) => $.product_quality["Variants"]) },
          },
        },
      ),
    ],
    [standardMetricIds, tagsEnhancedEnabled, t],
  );

  const defaultColumnVisibility = useMemo(
    () =>
      idsToVisibilityState(
        DEFAULT_VISIBLE_COLUMNS,
        columns.flatMap((column) => column.id ?? []),
      ),
    [columns],
  );

  const { state, setters, initialState } = usePersistedTableState({
    persistKey: "contentProductsTable",
    initialState: {
      sorting: [{ id: "seo_score", desc: true }],
      pagination: { pageIndex: 0, pageSize: 50 },
      columnVisibility: defaultColumnVisibility,
      columnOrder: DEFAULT_VISIBLE_COLUMNS,
      columnPinning: { start: ["product_title"], end: [] },
    },
  });

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const getProductOptions = useGetV4ProductReportByProduct(category);
  const productQuery = useBlockWindowQuery(getProductOptions, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });

  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());
  const tagEnhancedOptions = useTagFilterOptions("PRODUCT", { enabled: tagsEnhancedEnabled });

  const tagOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.product_quality["All Tags"]) },
      ...(productReportOptionsQuery.data?.data.tags.map((tag: string) => ({
        value: tag,
        label: tag,
      })) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags, t],
  );

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};
    if (!variations) return [];
    return [
      { value: "all", label: t(($) => $.product_quality["All Variants"]) },
      ...Object.keys(variations).flatMap((variationCategory) => {
        return (
          variations[variationCategory]?.map((variation: string) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          })) ?? []
        );
      }),
    ];
  }, [productReportOptionsQuery.data?.data, t]);

  const table = useServerTable({
    data: productQuery.data?.data,
    columns,
    initialState,
    state,
    ...setters,
    alternateSortingEnabled: true,
    getRowId: (row) => row.product_id.toString(),
    enableRowSelection: true,
    rowCount: productQuery.data?.count || 0,
    meta: {
      showSkeleton: productQuery.isFetching && (!productQuery.data || productQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        stock_status: [
          { value: "all", label: t(($) => $.product_quality["All Inventory States"]) },
          ...INVENTORY_STATE_OPTIONS.filter(Boolean),
        ],
        sales_state: [
          { value: "all", label: t(($) => $.product_quality["All Sales States"]) },
          ...SALE_STATE_OPTIONS.filter(Boolean),
        ],
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        variations: variantOptions.filter(Boolean),
      },
    },
  });

  const selected = selectionStateToSelectedIds(table.state.rowSelection);

  return (
    <div>
      <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
        <BulkActionButton isDisabled={!userCanUpdateProducts} selected={selected} options={bulkActionOptions} />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} className={css({ minWidth: "100%" })} />
    </div>
  );
};
